import { AlertTriangle, ArrowLeft, KeyRound, RefreshCw } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { cn } from '@/shared/utils/utils';

import { ApiError, getPermissionRequestContext, normalizeError } from './app-error';
import { showPermissionRequestPrompt } from './permission-request-events';

interface ErrorStateProps {
  error?: unknown;
  title?: string;
  message?: string;
  reportId?: string;
  onRetry?: () => void;
  onBack?: () => void;
  variant?: 'inline' | 'page';
  className?: string;
}

export const ErrorState = ({
  error,
  title,
  message,
  reportId,
  onRetry,
  onBack,
  variant = 'inline',
  className,
}: ErrorStateProps) => {
  const normalized = error === undefined ? undefined : normalizeError(error);
  const displayMessage = message ?? normalized?.userMessage ?? 'Please try again.';
  const permissionRequest = normalized
    ? getPermissionRequestContext(normalized)
    : undefined;
  const displayTitle =
    title ?? (permissionRequest ? 'Permission required' : 'We could not load this screen');
  const reference =
    reportId ??
    (normalized instanceof ApiError
      ? normalized.backendReportId ?? normalized.requestId ?? normalized.reportId
      : normalized?.reportId);
  const status = normalized instanceof ApiError ? normalized.status : undefined;

  return (
    <div
      className={cn(
        variant === 'page' &&
          'flex min-h-screen items-center justify-center bg-background p-4 text-foreground',
        className
      )}
    >
      <Card className={cn('w-full', variant === 'page' ? 'max-w-xl' : 'shadow-none')}>
        <CardHeader className="gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-[13px] border border-destructive/30 bg-destructive/10 text-destructive">
            <AlertTriangle aria-hidden="true" className="h-5 w-5" />
          </div>
          <div className="space-y-1.5">
            <CardTitle>{displayTitle}</CardTitle>
            <CardDescription className="text-base leading-relaxed text-foreground/80">
              {displayMessage}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(status || normalized?.code || reference) && (
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {status && (
                <span className="rounded-full border border-border bg-muted px-2.5 py-1">
                  HTTP {status}
                </span>
              )}
              {normalized?.code && (
                <span className="rounded-full border border-border bg-muted px-2.5 py-1">
                  {normalized.code}
                </span>
              )}
              {reference && (
                <span
                  className="rounded-full border border-border bg-muted px-2.5 py-1"
                  data-testid="error-reference"
                >
                  Reference {reference.slice(0, 12)}
                </span>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {permissionRequest?.canRequest && (
              <Button
                onClick={() => showPermissionRequestPrompt(normalized)}
                disabled={permissionRequest.requestStatus === 'pending'}
              >
                <KeyRound aria-hidden="true" />
                {permissionRequest.requestStatus === 'pending'
                  ? 'Access request pending'
                  : 'Request access'}
              </Button>
            )}
            {onRetry && (
              <Button onClick={onRetry}>
                <RefreshCw aria-hidden="true" />
                Try again
              </Button>
            )}
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft aria-hidden="true" />
                Go back
              </Button>
            )}
          </div>
          {import.meta.env.DEV && normalized && (
            <details className="rounded-[13px] border border-border bg-muted p-3 text-xs text-muted-foreground">
              <summary className="cursor-pointer font-semibold text-foreground">
                Developer details
              </summary>
              <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap font-mono">
                {normalized.stack ?? normalized.message}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
